/**
 * App.tsx - Application principale ServUp Administration
 * Utilise un layout avec menu latéral vertical
 */

import { useState, useEffect } from 'react';
import { useAuth, useUser } from './SupabaseWrapper';
import MainLayout from './components/layout/MainLayout';
import { MenuId } from './components/layout/Sidebar';
import UsersManagementPage from './pages/UsersManagementPage';
import TechniciensPage from './pages/TechniciensPage';
import AdminsPage from './pages/AdminsPage';
import ConfigMappingPage from './pages/ConfigMappingPage';
import SyncMonitoringPage from './pages/SyncMonitoringPage';
import PiecesPage from './pages/PiecesPage';
import InterventionsPage from './pages/InterventionsPage';
import SharingSettingsPage from './pages/SharingSettingsPage';
import RolesManagementPage from './pages/RolesManagementPage';
import JournalEnvoisPage from './pages/JournalEnvoisPage';
import { RESPONSIVE_CSS } from './styles/responsive';

// Configuration des pages
const PAGE_CONFIG: Record<MenuId, { title: string; subtitle: string }> = {
  users: {
    title: 'Gestion des utilisateurs',
    subtitle: 'Comptes actifs, techniciens et administrateurs'
  },
  techniciens: {
    title: 'Techniciens AS400',
    subtitle: 'Liste des sous-traitants depuis IBM i'
  },
  admins: {
    title: 'Gestion des accès',
    subtitle: 'Autorisez les utilisateurs à accéder à cette interface'
  },
  config: {
    title: 'Configuration',
    subtitle: 'Paramétrage des statuts intervention et autres configurations'
  },
  envois: {
    title: 'Journal des envois',
    subtitle: 'Courriels et SMS partis de la plateforme : état, fournisseur, motif d\'échec'
  },
  sync: {
    title: 'Logs',
    subtitle: 'Monitoring technique et logs de synchronisation'
  },
  pieces: {
    title: 'Pièces détachées',
    subtitle: 'Catalogue des pièces détachées'
  },
  interventions: {
    title: 'Ordres de réparation',
    subtitle: 'Tableau de bord des OR'
  },
  sharing: {
    title: 'Partage des ressources',
    subtitle: 'Configurez le périmètre de chaque ressource entre établissements'
  },
  roles: {
    title: 'Gestion des rôles',
    subtitle: 'Configurer les rôles et leurs permissions'
  }
};

function App() {
  const { logout } = useAuth();
  const user = useUser();

  // Menu actif - persisté dans localStorage
  const [activeMenu, setActiveMenu] = useState<MenuId>(() => {
    const saved = localStorage.getItem('sav_activeMenu');
    console.log('[App] Initial activeMenu from localStorage:', saved);
    return (saved as MenuId) || 'users';
  });

  // Compteur pour forcer le refresh des pages au changement d'onglet
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sauvegarder le menu actif dans localStorage et incrémenter le refresh trigger
  useEffect(() => {
    console.log('[App] Saving activeMenu to localStorage:', activeMenu);
    localStorage.setItem('sav_activeMenu', activeMenu);
    // Incrémenter le trigger pour forcer le refresh de la page active
    setRefreshTrigger(prev => prev + 1);
  }, [activeMenu]);

  // Obtenir les infos de la page active
  const pageInfo = PAGE_CONFIG[activeMenu] || PAGE_CONFIG.users;

  // Rendu du contenu selon le menu actif
  // Passe refreshTrigger pour que les pages rechargent leurs données au changement d'onglet
  const renderContent = () => {
    console.log('[App] Rendering content for menu:', activeMenu, 'refreshTrigger:', refreshTrigger);

    switch (activeMenu) {
      case 'users':
        return <UsersManagementPage refreshTrigger={refreshTrigger} />;
      case 'techniciens':
        return <TechniciensPage refreshTrigger={refreshTrigger} />;
      case 'admins':
        return <AdminsPage refreshTrigger={refreshTrigger} />;
      case 'config':
        return <ConfigMappingPage refreshTrigger={refreshTrigger} />;
      case 'sync':
        return <SyncMonitoringPage refreshTrigger={refreshTrigger} />;
      case 'pieces':
        return <PiecesPage refreshTrigger={refreshTrigger} />;
      case 'interventions':
        return <InterventionsPage refreshTrigger={refreshTrigger} />;
      case 'sharing':
        return <SharingSettingsPage refreshTrigger={refreshTrigger} />;
      case 'envois':
        return <JournalEnvoisPage refreshTrigger={refreshTrigger} />;
      case 'roles':
        return <RolesManagementPage refreshTrigger={refreshTrigger} />;
      default:
        return <UsersManagementPage refreshTrigger={refreshTrigger} />;
    }
  };

  return (
    <MainLayout
      activeMenu={activeMenu}
      setActiveMenu={setActiveMenu}
      pageTitle={pageInfo.title}
      pageSubtitle={pageInfo.subtitle}
      user={user}
      onLogout={logout}
    >
      {renderContent()}
      <style>{RESPONSIVE_CSS}</style>
    </MainLayout>
  );
}

export default App;
